import { canvasHeight, canvasWidth, initialZoom, Themes } from './data/constants';
import { Camera } from './camera';
import { StageDef } from './data/maps';
import { Marble } from './marble';
import { ParticleManager } from './particleManager';
import { GameObject } from './gameObject';
import { UIObject } from './UIObject';
import { VectorLike } from './types/VectorLike';
import { MapEntityState } from './types/MapEntity.type';
import { ColorTheme } from './types/ColorTheme';
import { fetchSlackUsers, matchSlackUser, getSlackImageUrl, SlackUser } from './slackApi';

export type RenderParameters = {
  camera: Camera;
  stage: StageDef;
  entities: MapEntityState[];
  marbles: Marble[];
  allMarbles?: Marble[];
  winners: Marble[];
  particleManager: ParticleManager;
  effects: GameObject[];
  winnerRank: number;
  winner: Marble | null;
  winnerCount?: number;
  size: VectorLike;
  theme: ColorTheme;
};

export class RouletteRenderer {
  private _canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  public sizeFactor = 1;

  private _images: { [key: string]: HTMLImageElement } = {};
  private _slackUsers: SlackUser[] = [];
  private _slackUserMap: Map<string, SlackUser> = new Map(); // 이름 -> SlackUser 매핑
  private _theme: ColorTheme = Themes.dark;

  constructor() {
  }

  get width() {
    return this._canvas.width;
  }

  get height() {
    return this._canvas.height;
  }

  get canvas() {
    return this._canvas;
  }

  set theme(value: ColorTheme) {
    this._theme = value;
  }

  async init() {
    this._canvas = document.createElement('canvas');
    this._canvas.width = canvasWidth;
    this._canvas.height = canvasHeight;
    this.ctx = this._canvas.getContext('2d', {
      alpha: false,
    }) as CanvasRenderingContext2D;

    document.body.appendChild(this._canvas);

    const resizing = (entries?: ResizeObserverEntry[]) => {
      const realSize = entries
        ? entries[0].contentRect
        : this._canvas.getBoundingClientRect();
      const width = Math.max(realSize.width / 2, 640);
      const height = (width / realSize.width) * realSize.height;
      this._canvas.width = width;
      this._canvas.height = height;
      this.sizeFactor = width / realSize.width;
    };

    const resizeObserver = new ResizeObserver(resizing);

    resizeObserver.observe(this._canvas);
    resizing();
  }

  private async _loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((rs) => {
      const img = new Image();
      img.addEventListener('load', () => {
        rs(img);
      });
      img.src = url;
    });
  }

  /**
   * Slack API를 사용하여 사용자 정보를 로드하고 이미지를 매칭합니다
   * @param apiBaseUrl 서버 API 기본 URL (선택사항, 기본값: '/api')
   */
  public async loadSlackUsers(apiBaseUrl: string = '/api'): Promise<void> {
    try {
      this._slackUsers = await fetchSlackUsers(apiBaseUrl);
      this._slackUserMap.clear();

      // 각 Slack 사용자를 이름별로 매핑
      this._slackUsers.forEach((user) => {
        const names = [
          user.name,
          user.real_name,
          user.display_name,
        ].filter(Boolean);

        names.forEach((name) => {
          if (name) {
            const normalizedName = name.toLowerCase();
            // 이미 매핑이 없으면 추가 (첫 번째 매칭 우선)
            if (!this._slackUserMap.has(normalizedName)) {
              this._slackUserMap.set(normalizedName, user);
            }
          }
        });
      });

      console.log(`Loaded ${this._slackUsers.length} Slack users`);
    } catch (error) {
      console.error('Failed to load Slack users:', error);
      throw error;
    }
  }

  /**
   * 특정 이름에 대한 Slack 이미지를 로드합니다
   * @param name 구슬 이름
   */
  private async _loadSlackImageForName(name: string): Promise<void> {
    // 이미 로드된 이미지가 있으면 스킵
    if (this._images[name]) {
      return;
    }

    // Slack 사용자 매칭 시도
    const matchedUser = matchSlackUser(name, this._slackUsers);

    if (matchedUser) {
      const imageUrl = getSlackImageUrl(matchedUser, '192');
      if (imageUrl) {
        try {
          this._images[name] = await this._loadImage(imageUrl);
          console.log(`Loaded Slack image for: ${name}`);
        } catch (error) {
          console.warn(`Failed to load Slack image for ${name}:`, error);
        }
      }
    }
  }

  /**
   * 여러 이름에 대한 Slack 이미지를 일괄 로드합니다
   * @param names 구슬 이름 배열
   */
  public async loadSlackImagesForNames(names: string[]): Promise<void> {
    const uniqueNames = [...new Set(names)];
    const loadPromises = uniqueNames.map((name) =>
      this._loadSlackImageForName(name)
    );
    await Promise.all(loadPromises);
  }


  render(renderParameters: RenderParameters, uiObjects: UIObject[]) {
    this._theme = renderParameters.theme;
    this.ctx.fillStyle = this._theme.background;
    this.ctx.fillRect(0, 0, this._canvas.width, this._canvas.height);

    this.ctx.save();
    this.ctx.scale(initialZoom, initialZoom);
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';
    this.ctx.font = '0.4pt sans-serif';
    this.ctx.lineWidth = 3 / (renderParameters.camera.zoom + initialZoom);
    renderParameters.camera.renderScene(this.ctx, () => {
      this.renderEntities(renderParameters.entities);
      this.renderEffects(renderParameters);
      this.renderMarbles(renderParameters);
    });
    this.ctx.restore();

    uiObjects.forEach((obj) =>
      obj.render(
        this.ctx,
        renderParameters,
        this._canvas.width,
        this._canvas.height,
      ),
    );
    renderParameters.particleManager.render(this.ctx);
    this.renderWinner(renderParameters);
  }

  private renderEntities(entities: MapEntityState[]) {
    this.ctx.save();
    entities.forEach((entity) => {
      const transform = this.ctx.getTransform();
      this.ctx.translate(entity.x, entity.y);
      this.ctx.rotate(entity.angle);
      this.ctx.fillStyle = entity.shape.color ?? this._theme.entity[entity.shape.type].fill;
      this.ctx.strokeStyle = entity.shape.color ?? this._theme.entity[entity.shape.type].outline;
      this.ctx.shadowBlur = this._theme.entity[entity.shape.type].bloomRadius;
      this.ctx.shadowColor = entity.shape.bloomColor ?? entity.shape.color ?? this._theme.entity[entity.shape.type].bloom;
      const shape = entity.shape;
      switch (shape.type) {
        case 'polyline':
          if (shape.points.length > 0) {
            this.ctx.beginPath();
            this.ctx.moveTo(shape.points[0][0], shape.points[0][1]);
            for (let i = 1; i < shape.points.length; i++) {
              this.ctx.lineTo(shape.points[i][0], shape.points[i][1]);
            }
            this.ctx.stroke();
          }
          break;
        case 'box':
          const w = shape.width * 2;
          const h = shape.height * 2;
          this.ctx.rotate(shape.rotation);
          this.ctx.fillRect(-w / 2, -h / 2, w, h);
          this.ctx.strokeRect(-w / 2, -h / 2, w, h);
          break;
        case 'circle':
          this.ctx.beginPath();
          this.ctx.arc(0, 0, shape.radius, 0, Math.PI * 2, false);
          this.ctx.stroke();
          break;
      }

      this.ctx.setTransform(transform);
    });
    this.ctx.restore();
  }

  private renderEffects({ effects, camera }: RenderParameters) {
    effects.forEach((effect) =>
      effect.render(this.ctx, camera.zoom * initialZoom, this._theme),
    );
  }

  private renderMarbles({
    marbles,
    camera,
    winnerRank,
    winners,
    size,
  }: RenderParameters) {
    const winnerIndex = winnerRank - winners.length;

    const viewPort = { x: camera.x, y: camera.y, w: size.x, h: size.y, zoom: camera.zoom * initialZoom };
    marbles.forEach((marble, i) => {
      marble.render(
        this.ctx,
        camera.zoom * initialZoom,
        i === winnerIndex,
        false,
        this._images[marble.name] || undefined,
        viewPort,
        this._theme,
      );
    });
  }

  private renderWinner({ winner, winners, winnerCount, theme }: RenderParameters) {
    if (!winner && (!winners || winners.length === 0)) return;

    // 승자 표시 시에만 중복 제거 (같은 이름의 구슬 중 첫 번째만 표시)
    const uniqueWinners = winners && winners.length > 0
      ? winners.filter((w, index, self) =>
        index === self.findIndex(winner => winner.name === w.name)
      )
      : (winner ? [winner] : []);

    const displayWinners = uniqueWinners.slice(0, winnerCount || 1);
    const count = displayWinners.length;

    this.ctx.save();
    this.ctx.fillStyle = theme.winnerBackground;
    const winnerHeight = count > 1 ? Math.min(168 + (count - 1) * 60, 300) : 168;
    this.ctx.fillRect(
      this._canvas.width / 2,
      this._canvas.height - winnerHeight,
      this._canvas.width / 2,
      winnerHeight,
    );
    this.ctx.fillStyle = theme.winnerText;
    this.ctx.strokeStyle = theme.winnerOutline;

    this.ctx.font = 'bold 48px sans-serif';
    this.ctx.textAlign = 'right';
    this.ctx.lineWidth = 4;

    // Winner/Winners 텍스트
    const winnerText = count > 1 ? 'Winners' : 'Winner';
    if (theme.winnerOutline) {
      this.ctx.strokeText(
        winnerText,
        this._canvas.width - 10,
        this._canvas.height - winnerHeight + 50,
      );
    }
    this.ctx.fillText(
      winnerText,
      this._canvas.width - 10,
      this._canvas.height - winnerHeight + 50,
    );

    // 승자 이름들 표시
    this.ctx.font = 'bold 72px sans-serif';
    displayWinners.slice(0, count).forEach((w, index) => {
      const yPos = this._canvas.height - winnerHeight + 120 + (index * 80);
      const marbleSize = 50; // 구슬 크기
      const textX = this._canvas.width - 10; // 텍스트 오른쪽 끝 위치

      // 텍스트 너비 측정
      const textWidth = this.ctx.measureText(w.name).width;
      const marbleX = textX - textWidth - marbleSize - 5; // 텍스트 왼쪽에 구슬 배치
      const marbleY = yPos - 30; // 텍스트 기준 약간 위쪽

      // 구슬 모양 그리기 (텍스트 왼쪽에)
      this.ctx.save();
      const marbleImage = this._images[w.name]; // 특수 구슬 이미지 확인
      const radius = marbleSize / 2;

      if (marbleImage) {
        // 이미지가 있는 경우 이미지로 표시 (원형으로 클리핑)
        const imageSize = marbleSize;

        // 원형 클리핑 경로 생성
        this.ctx.beginPath();
        this.ctx.arc(marbleX, marbleY, radius, 0, Math.PI * 2);
        this.ctx.clip();

        // 이미지 그리기
        this.ctx.drawImage(
          marbleImage,
          marbleX - imageSize / 2,
          marbleY - imageSize / 2,
          imageSize,
          imageSize,
        );

        // 클리핑 해제를 위해 restore 전에 다시 경로 설정
        this.ctx.restore();
        this.ctx.save();

        // 구슬 외곽선 (선택적)
        if (theme.winnerOutline) {
          this.ctx.strokeStyle = theme.winnerOutline;
          this.ctx.lineWidth = 2;
          this.ctx.beginPath();
          this.ctx.arc(marbleX, marbleY, radius, 0, Math.PI * 2);
          this.ctx.stroke();
        }
      } else {
        // 이미지가 없는 경우 색상으로 표시
        this.ctx.fillStyle = `hsl(${w.hue} 100% ${theme.marbleLightness}%)`;
        this.ctx.beginPath();
        this.ctx.arc(marbleX, marbleY, radius, 0, Math.PI * 2);
        this.ctx.fill();

        // 구슬 외곽선 (선택적)
        if (theme.winnerOutline) {
          this.ctx.strokeStyle = theme.winnerOutline;
          this.ctx.lineWidth = 2;
          this.ctx.stroke();
        }
      }
      this.ctx.restore();

      // 승자 이름 텍스트
      this.ctx.fillStyle = `hsl(${w.hue} 100% ${theme.marbleLightness}`;
      if (theme.winnerOutline) {
        this.ctx.strokeText(
          w.name,
          textX,
          yPos,
        );
      }
      this.ctx.fillText(
        w.name,
        textX,
        yPos,
      );
    });

    this.ctx.restore();
  }
}
